import { ocKey, seatKey, seatWeight, type OcSens } from './oc-learn';
import { seatNeed, type SeatNeed } from './oc-need';
import { roleTag } from './oc-roles';
import { tpKey } from './oc-success';

/**
 * OC seat suitability (owner's request 2026-10-22: "I just want OCs to be better"; docs/oc-suitability.md): one policy
 * for every place that judges a member in an organized-crime seat — the Crimes tab, the OC card and the Discord bot.
 * Pure. Three separate steps:
 *  1. thresholds — what a seat needs (`seatThresholds`: a minimum and a preferred target, from the rules you set or the
 *     weight-graded default, with where each came from);
 *  2. a status — `judgeSeat`: UNSUITABLE (under the minimum), RISKY (meets the minimum, under the target), SUITABLE
 *     (meets the target), UNVERIFIED (a pass rate or a threshold is missing — never treated as passing);
 *  3. words — `explainSeat`: everything leadership needs to act, and on what basis it was judged.
 * Lineup changes are worked out apart from this (oc-lineup.ts).
 *
 * What this is NOT: a chance of success. A pass rate (CPR) is the chance to pass one checkpoint; how many checkpoints a
 * seat faces, which failures end the crime and which only cut the payout aren't known to Omni-Torn, so nothing here
 * multiplies rates into a probability. A crime's chance of success is tornprobability.com's calculation (oc-success.ts),
 * shown beside these judgements, never mixed into them.
 */

export type OcStatus = 'UNSUITABLE' | 'RISKY' | 'SUITABLE' | 'UNVERIFIED';

/** How each status reads in the UI (the same words everywhere). */
export const OC_STATUS_LABEL: Record<OcStatus, string> = { UNSUITABLE: 'Unsuitable', RISKY: 'Risky', SUITABLE: 'Suitable', UNVERIFIED: 'Unverified' };

/** Worst first. */
const STATUS_RANK: Record<OcStatus, number> = { UNSUITABLE: 0, RISKY: 1, UNVERIFIED: 2, SUITABLE: 3 };

/** When nobody available meets a seat's minimum: suggest the closest (clearly marked), or only warn. */
export type NoneMeets = 'closest' | 'warn';

/**
 * Defaults (owner, 2026-10-22: his faction's guidelines are the built-in default for every Omni-Torn user — they're the
 * rules in DEFAULT_OC_RULES; these numbers fill in where those rules say nothing):
 *  - low 65 / good 75: crimes the guidelines don't list (level 7+ apart from the three named ones, and the "carries only"
 *    seats) are graded by role weight from 65% for a seat of average weight (the guidelines' rate for levels 2–6, the
 *    closest listed) up to close to 75% for the heaviest;
 *  - margin 5: where a rule gives only a minimum, the preferred target is the minimum + 5 ("Risky" = within 5 points of
 *    the minimum);
 *  - a member's pass rate seen more than 14 days ago isn't used to suggest them (rates rise as members do crimes).
 */
export const OC_POLICY_DEFAULTS = { low: 0, good: 75, margin: 5, noneMeets: 'closest' as NoneMeets, strictFrom: 0, criticalFirst: true, freshDays: 14 };

/** Where a rule line came from: the built-in default (the guidelines), your faction's published rules, or your own. */
export type OcRuleSource = 'builtin' | 'faction' | 'own';

/** One rule you set: a crime, a role or seat in it (or a role in any crime, or crimes of some levels), and a minimum and/or target. */
export interface OcRule {
  /** ocKey of the crime ("breakthebank"); '' = any crime. */
  scenario: string;
  /** seatKey ("thief2") when `seat`, else ocKey of the role ("thief"); '' = the whole crime. */
  role: string;
  /** The rule names one seat ("Thief #2"), not every seat of the role. */
  seat: boolean;
  min: number | null;
  target: number | null;
  /** The line as written (to show where a threshold came from). */
  text: string;
  /** "level 2-6: 65": every seat of crimes of these levels (inclusive), flat (not graded). Null otherwise. */
  levels?: [number, number] | null;
  /** The line's note ("# CPR boost role (4m pay)"), shown with the seat. */
  note?: string;
  source?: OcRuleSource;
}

/** "Break the Bank > Muscle #3: carries only" — the seat is kept for the faction's main carries ("anyone" undoes it). */
export interface OcSeatFlag {
  scenario: string;
  role: string;
  seat: boolean;
  reserved: boolean;
  text: string;
  note?: string;
  source?: OcRuleSource;
}

/** "Clinical Precision > Imitator, Cleaner: after Cat Burglar, Assassin" — join these only once those are filled. */
export interface OcOrderRule {
  scenario: string;
  seats: Array<{ role: string; seat: boolean }>;
  /** The seats to fill first, as written ("Cat Burglar"); empty = "any order" (undoes an earlier one). */
  after: string[];
  text: string;
  note?: string;
  source?: OcRuleSource;
}

/** "prefer: Break the Bank, level 8" — crimes (ocKeys) and crime levels members are steered to when they qualify. */
export interface OcPrefer {
  crimes: string[];
  levels: Array<[number, number]>;
  text: string;
  note?: string;
}

/** "carries: 75+" / "carries: 2282863, 12345" — who counts as one of the faction's main carries. */
export interface OcCarries {
  ids: number[];
  /** A pass rate of at least this in the seat makes a carry (null: only the listed ids). */
  min: number | null;
}

/**
 * Requirements worked out from tornprobability.com's success model to match the guidelines' chance of success
 * (oc-requirements.ts; owner, round 3: "make a blanket requirement but use our requirements as a baseline"): per crime
 * (tpKey of its name), per seat (tpKey: "Muscle #1" = "Muscle 1"), a minimum and a target. Used only where no rule says
 * anything (after level rules, before the general rate).
 */
export interface OcDerived {
  crimes: Record<
    string,
    {
      seats: Record<string, { min: number; target: number }>;
      relaxed: boolean;
      /** The chance of success this crime's numbers were matched to, at the minimums / targets (0…1; else the overall ones). */
      minSuccess?: number;
      targetSuccess?: number;
      /** Which of the rules' levels that chance came from. */
      basis?: OcGoalBasis | null;
      /** Whose rules it was matched to, where not the document's (a crime only the server could work out). */
      source?: OcRuleSource;
    }
  >;
  /** The chance of success the rules accept at the minimum / target across their crimes (0…1). */
  minSuccess: number;
  targetSuccess: number;
  /** When they were derived (ms). */
  at: number;
  /** Whose rules the chance of success was matched to: the built-in guidelines, your faction's, or yours. */
  source?: OcRuleSource;
}

/**
 * Where a crime's accepted chance of success comes from (oc-requirements.ts goalsFor; owner, 2026-10-22: "Success chance
 * percent should be derivable"): what the rules accept for crimes of its level; between two levels they cover (in
 * proportion); the nearest level they cover (crimes above or below every covered level — no trend is assumed); across
 * all their crimes (the crime's level isn't known); or a fixed level set on the server (an expert override).
 */
export type OcGoalBasis = { kind: 'level'; level: number } | { kind: 'between'; lo: number; hi: number } | { kind: 'nearest'; level: number } | { kind: 'all' } | { kind: 'fixed' };

export interface OcPolicy {
  /** What a seat of average weight needs (the graded default's minimum); 0 = no general minimum. */
  low: number;
  /** A good pass rate: the heaviest seats need close to it. */
  good: number;
  rules: readonly OcRule[];
  noneMeets: NoneMeets;
  /** From this crime level up, nobody qualifying is only warned about, never filled with the closest (0: never). */
  strictFrom: number;
  /** The critical role's problems are listed first and weigh more in lineup suggestions. */
  criticalFirst: boolean;
  /** A rule with only a minimum: target = minimum + this (null: no target from it). */
  margin: number | null;
  flags: readonly OcSeatFlag[];
  order: readonly OcOrderRule[];
  prefer: OcPrefer | null;
  carries: OcCarries | null;
  /** Requirements derived from tornprobability's model (null: none known). */
  derived: OcDerived | null;
}

export function ocPolicy(p: Partial<OcPolicy> = {}): OcPolicy {
  const d = OC_POLICY_DEFAULTS;
  return {
    low: p.low ?? d.low,
    good: p.good ?? d.good,
    rules: p.rules ?? [],
    noneMeets: p.noneMeets ?? d.noneMeets,
    strictFrom: p.strictFrom ?? d.strictFrom,
    criticalFirst: p.criticalFirst ?? d.criticalFirst,
    margin: p.margin === undefined ? d.margin : p.margin,
    flags: p.flags ?? [],
    order: p.order ?? [],
    prefer: p.prefer ?? null,
    carries: p.carries ?? null,
    derived: p.derived ?? null,
  };
}

// ---------------------------------------------------------------- the built-in default: a faction's guidelines

/**
 * The built-in default for every Omni-Torn user (owner, 2026-10-22: "It should be a default across for all omni torn
 * users. The baseline should be editable by anyone for their own client") — "Omni-Torn's default OC guidelines" (public
 * bot plan, 2026-11-01: no faction or player is named in them; main carries are a pass rate, never names), as rule lines.
 * Seat names are Torn's own ("Picklock #1" for his "Pick Lock 1", "Muscle #3" for "Muscle 3"; checked on Torn's crime
 * list). Levels 7+ are covered crime by crime, not by level (owner, 2026-10-22: "L7 and up are there just with specific for
 * each scenario instead of a broad L7 has this requirement"): a level 7+ crime without lines of its own has no rule —
 * its seats are Unverified ("no rule"), unless the player (or faction) sets a general rate (OC_POLICY_DEFAULTS.low 0 =
 * none). The "carries only" seats: a carry is fine there, anyone else Risky. Your own lines and your faction's published rules come
 * on top (buildOcPolicy).
 */
export const DEFAULT_OC_RULES = `# Omni-Torn's default OC guidelines, for every Omni-Torn user (no faction or player is named; seats no line covers follow Omni-Torn's derived requirements).
# Minimum pass rate by crime level — crimes with their own lines below follow those.
level 1: any
level 2-6: 65
# Level 7+: crime by crime, below (a level 7+ crime without lines of its own has no rule: its seats show "no rule").
prefer: Break the Bank, level 8   # highest paying by far: join them if you have the pass rate
carries: 75+   # the faction's main carries: 75%+ in that seat (or list their ids: carries: 123, 456)
Break the Bank > Muscle #3, Thief #2: carries only   # blocked for main carries
Break the Bank > Muscle #1, Robber, Muscle #2: 55
Break the Bank > Thief #1: 30   # CPR boost role (4m pay)
Blast from the Past > Muscle, Bomber: 65
Blast from the Past > Hacker: 60
Blast from the Past > Engineer: 50
Blast from the Past > Picklock #1: 40
Blast from the Past > Picklock #2: 30
Clinical Precision > Imitator, Cleaner: 65   # they decide the share of the winnings
Clinical Precision > Cat Burglar: 45   # CPR boost role (7.5m pay)
Clinical Precision > Assassin: 35   # CPR boost role (7.5m pay)
Clinical Precision > Imitator, Cleaner: after Cat Burglar, Assassin   # don't get carry members stuck in a Clinical Precision`;

// ---------------------------------------------------------------- rules (written as lines of text)

export interface OcRuleErrors {
  errors: Array<{ line: number; text: string; why: string }>;
}

/** Everything a rules text says. */
export interface OcRuleSet extends OcRuleErrors {
  rules: OcRule[];
  flags: OcSeatFlag[];
  order: OcOrderRule[];
  prefer: OcPrefer | null;
  carries: OcCarries | null;
}

const LEVEL = /^(?:level|lvl|l)\s*(\d{1,2})\s*(?:(?:-|to)\s*(\d{1,2})|(\+))?$/i;
const levelRange = (m: RegExpExecArray): [number, number] => {
  const a = Number(m[1]);
  return [a, m[3] ? 99 : m[2] !== undefined ? Number(m[2]) : a];
};
const rolePart = (r: string) => {
  const seat = /#\s*\d+$/.test(r);
  return { role: seat ? seatKey(r) : ocKey(r), seat };
};
const list = (s: string) =>
  s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Rules as you write them, one per line (`#` followed by a space or a word starts a note, kept and shown with the seat;
 * "#2" right after a role is its seat number):
 *   Break the Bank > Thief #2: 70 / 80           one seat of one crime — minimum 70, target 80
 *   Break the Bank > Muscle #1, Robber: 55       several seats/roles at once — minimum only (target = minimum + margin)
 *   any > Hacker: - / 80                         every Hacker seat in any crime — target only
 *   Break the Bank: 65 / 80                      the whole crime: its own "low" and "good" rates, graded by each seat's weight
 *   level 2-6: 65   ·   level 1: any   ·   level 7+: 70     every seat of crimes of these levels, flat
 *   Break the Bank > Muscle #3: carries only     kept for the faction's main carries ("anyone" undoes it)
 *   Clinical Precision > Imitator: after Cat Burglar, Assassin    join it only once those are filled ("any order" undoes it)
 *   prefer: Break the Bank, level 8              steer members who qualify to these crimes / levels
 *   carries: 75+   ·   carries: 2282863, 12345   who counts as a main carry (a pass rate in the seat, and/or player ids)
 */
export function parseOcRules(text: string, source: OcRuleSource = 'own'): OcRuleSet {
  const out: OcRuleSet = { rules: [], flags: [], order: [], prefer: null, carries: null, errors: [] };
  String(text ?? '')
    .split(/\r?\n/)
    .forEach((raw, i) => {
      const cut = raw.search(/#(?!\d)/);
      const line = (cut >= 0 ? raw.slice(0, cut) : raw).trim();
      const note = cut >= 0 ? raw.slice(cut + 1).trim() : '';
      if (!line) return;
      const bad = (why: string) => out.errors.push({ line: i + 1, text: raw.trim(), why });
      const colon = line.indexOf(':');
      if (colon < 0) return bad('write it as “Crime > Role: minimum / target”');
      const head = line.slice(0, colon).trim();
      const value = line.slice(colon + 1).trim();
      const extra = { text: line, source, ...(note ? { note } : {}) };
      // prefer: crimes and levels
      if (/^prefer$/i.test(head)) {
        const crimes: string[] = [];
        const levels: Array<[number, number]> = [];
        for (const item of list(value)) {
          const lm = LEVEL.exec(item);
          if (lm) levels.push(levelRange(lm));
          else crimes.push(ocKey(item));
        }
        if (!crimes.length && !levels.length) return bad('name crimes or levels: “prefer: Break the Bank, level 8”');
        out.prefer = { crimes, levels, text: line, ...(note ? { note } : {}) };
        return;
      }
      // carries: ids and/or a pass rate
      if (/^carr(y|ies)$/i.test(head)) {
        if (/^none$/i.test(value)) {
          out.carries = { ids: [], min: null };
          return;
        }
        const c: OcCarries = { ids: out.carries?.ids ?? [], min: out.carries?.min ?? null };
        const ids: number[] = [];
        for (const item of list(value)) {
          const rate = /^(?:cpr\s*)?(\d{1,3})\s*%?\s*\+$/i.exec(item);
          if (rate && Number(rate[1]) <= 100) c.min = Number(rate[1]);
          else if (/^\d{1,9}$/.test(item) && Number(item) > 0) ids.push(Number(item));
          else return bad('“carries: 75+” (a pass rate) or “carries: 123, 456” (player ids)');
        }
        if (ids.length) c.ids = ids;
        out.carries = c;
        return;
      }
      const nums = /^(-|\d{1,3})?\s*(?:\/\s*(-|\d{1,3}))?$/.exec(value);
      const val = (s: string | undefined) => (s === undefined || s === '-' ? null : Number(s));
      const anyRate = /^any$/i.test(value);
      const readNums = (): { min: number | null; target: number | null } | string => {
        if (anyRate) return { min: 0, target: 0 };
        if (!nums) return 'write the minimum and target as numbers: “65” or “65 / 75”';
        const min = val(nums[1]);
        const target = val(nums[2]);
        if (min === null && target === null) return 'no minimum or target';
        if ((min !== null && min > 100) || (target !== null && target > 100)) return 'pass rates go from 0 to 100';
        return { min, target };
      };
      // level 2-6: 65
      const lm = LEVEL.exec(head);
      if (lm) {
        const n = readNums();
        if (typeof n === 'string') return bad(n);
        out.rules.push({ scenario: '', role: '', seat: false, ...n, levels: levelRange(lm), ...extra });
        return;
      }
      const [crime, roleText = '', ...more] = head.split('>').map((x) => x.trim());
      if (more.length) return bad('one “>” only');
      const scenario = /^(any|\*|all)$/i.test(crime ?? '') ? '' : ocKey(crime ?? '');
      const roles = list(roleText).map(rolePart);
      // Seat kinds: carries only / anyone, after … / any order
      if (/^(carr(y|ies) only|reserved|blocked)$/i.test(value) || /^(anyone|open)$/i.test(value)) {
        if (!roles.length) return bad('name the seats: “Break the Bank > Muscle #3: carries only”');
        for (const r of roles) out.flags.push({ scenario, ...r, reserved: !/^(anyone|open)$/i.test(value), ...extra });
        return;
      }
      const after = /^after\s+(.+)$/i.exec(value);
      if (after || /^any order$/i.test(value)) {
        if (!roles.length || !scenario) return bad('name the crime and seats: “Clinical Precision > Imitator: after Cat Burglar”');
        out.order.push({ scenario, seats: roles, after: after ? list(after[1]!) : [], ...extra });
        return;
      }
      const n = readNums();
      if (typeof n === 'string') return bad(n === 'write the minimum and target as numbers: “65” or “65 / 75”' ? 'write it as “Crime > Role: minimum / target”' : n);
      if (!scenario && !roles.length) return bad('for every seat of every crime, use the “low” and “good” rates');
      if (!roles.length) out.rules.push({ scenario, role: '', seat: false, ...n, ...extra });
      for (const r of roles) out.rules.push({ scenario, ...r, ...n, ...extra });
    });
  return out;
}

/** Several rules texts, later ones on top (built-in default, then your faction's, then your own): one rule set. */
export function mergeRuleSets(...sets: OcRuleSet[]): OcRuleSet {
  const out: OcRuleSet = { rules: [], flags: [], order: [], prefer: null, carries: null, errors: [] };
  for (const s of sets) {
    out.rules.push(...s.rules);
    out.flags.push(...s.flags);
    out.order.push(...s.order);
    if (s.prefer) out.prefer = s.prefer;
    if (s.carries) out.carries = { ids: s.carries.ids.length || !out.carries ? s.carries.ids : out.carries.ids, min: s.carries.min ?? out.carries?.min ?? null };
    out.errors.push(...s.errors);
  }
  return out;
}

/** The numbers a faction publishes with its rules (null: not given). */
export interface OcNumbers {
  low?: number | null;
  good?: number | null;
  margin?: number | null;
}

/**
 * The policy everything judges by (owner, 2026-10-22: the guidelines are everyone's default, editable by anyone for
 * themselves, and a faction can sync its own): built-in default rules, then your faction's published rules (when there
 * are any and you follow them), then your own lines on top — a more specific line always wins, and of two equally
 * specific ones the later. Numbers: yours when you changed them from the default, else your faction's, else the default.
 */
export function buildOcPolicy(o: {
  own?: string;
  faction?: ({ text: string } & OcNumbers) | null;
  /** Follow the faction's rules (default: yes, when it published some). */
  followFaction?: boolean;
  /** Your own numbers (undefined/null: not set). */
  low?: number | null;
  good?: number | null;
  margin?: number | null;
  noneMeets?: NoneMeets;
  strictFrom?: number;
  criticalFirst?: boolean;
  /** Requirements derived from tornprobability's model (oc-requirements.ts derivedFromDoc), for seats no rule covers. */
  derived?: OcDerived | null;
}): { policy: OcPolicy; errors: OcRuleSet['errors']; fromFaction: boolean } {
  const fac = o.followFaction !== false && o.faction && typeof o.faction.text === 'string' ? o.faction : null;
  const base = fac ? parseOcRules(fac.text, 'faction') : parseOcRules(DEFAULT_OC_RULES, 'builtin');
  const own = parseOcRules(o.own ?? '', 'own');
  const set = mergeRuleSets(base, own);
  const d = OC_POLICY_DEFAULTS;
  const pickNum = (mine: number | null | undefined, theirs: number | null | undefined, def: number) => (typeof mine === 'number' && Number.isFinite(mine) && mine !== def ? mine : typeof theirs === 'number' && Number.isFinite(theirs) ? theirs : def);
  const policy = ocPolicy({
    low: pickNum(o.low, fac?.low, d.low),
    good: pickNum(o.good, fac?.good, d.good),
    margin: pickNum(o.margin, fac?.margin, d.margin),
    rules: set.rules,
    flags: set.flags,
    order: set.order,
    prefer: set.prefer,
    carries: set.carries,
    derived: o.derived ?? null,
    ...(o.noneMeets ? { noneMeets: o.noneMeets } : {}),
    ...(o.strictFrom !== undefined ? { strictFrom: o.strictFrom } : {}),
    ...(o.criticalFirst !== undefined ? { criticalFirst: o.criticalFirst } : {}),
  });
  // Lines that couldn't be read are reported for your own text only (the others aren't yours to fix here).
  return { policy, errors: own.errors, fromFaction: !!fac };
}

/** "Thief #2" → the rule line for it in that crime ("Break the Bank > Thief #2: 70 / 80"); null both = no rule. */
export function ruleLine(crimeName: string, roleName: string | null, min: number | null, target: number | null): string {
  return `${crimeName}${roleName ? ` > ${roleName}` : ''}: ${min ?? '-'}${target !== null ? ` / ${target}` : ''}`;
}

/**
 * Your rules text with the rule for one crime + seat (or role, or the whole crime when `roleName` is null) set — the
 * line replaced, added, or removed when both numbers are null. Other lines (and notes) stay as they were.
 */
export function setRuleInText(text: string, crimeName: string, roleName: string | null, min: number | null, target: number | null): string {
  const want = parseOcRules(ruleLine(crimeName, roleName, 0, 0)).rules[0];
  const lines = String(text ?? '').split(/\r?\n/);
  const same = (l: string) => {
    const got = parseOcRules(l).rules;
    return got.length === 1 && !!want && got[0]!.scenario === want.scenario && got[0]!.role === want.role && got[0]!.seat === want.seat && !got[0]!.levels;
  };
  const kept = lines.filter((l) => !same(l));
  if (min !== null || target !== null) kept.push(ruleLine(crimeName, roleName, min, target));
  return kept.filter((l, i, a) => l.trim() || i < a.length - 1).join('\n').replace(/^\n+/, '');
}

// ---------------------------------------------------------------- seat kinds: carries only, order, priorities

const matchesSeat = (r: { scenario: string; role: string; seat: boolean }, sc: string, sk: string, rk: string) => (r.scenario === '' || r.scenario === sc) && (r.seat ? r.role === sk : r.role === rk);
/** How specific a match is: seat in this crime 3, role in this crime 2, role in any crime 1. */
const specificity = (r: { scenario: string; seat: boolean }) => (r.scenario ? (r.seat ? 3 : 2) : 1);

/** Is this seat kept for the faction's main carries? (the most specific line wins; of equals, the later). */
export function seatReserved(p: Pick<OcPolicy, 'flags'>, crimeName: string, seatName: string): OcSeatFlag | null {
  const sc = ocKey(crimeName);
  let best: OcSeatFlag | null = null;
  for (const f of p.flags) if (matchesSeat(f, sc, seatKey(seatName), ocKey(seatName)) && (!best || specificity(f) >= specificity(best))) best = f;
  return best?.reserved ? best : null;
}

/** Is this member one of the faction's main carries (a listed id, or the carry pass rate in this seat)? */
export function isCarry(c: OcCarries | null, member: number | null | undefined, rate: number | null): boolean {
  if (!c) return false;
  if (member != null && c.ids.includes(member)) return true;
  return c.min !== null && rate !== null && rate >= c.min;
}

/** The seats this one should wait for ("after Cat Burglar, Assassin"), or [] — the last line covering it wins. */
export function seatOrder(p: Pick<OcPolicy, 'order'>, crimeName: string, seatName: string): { after: string[]; rule: OcOrderRule | null } {
  const sc = ocKey(crimeName);
  let got: OcOrderRule | null = null;
  for (const r of p.order) if (r.scenario === sc && r.seats.some((s) => matchesSeat({ scenario: sc, ...s }, sc, seatKey(seatName), ocKey(seatName)))) got = r;
  return { after: got?.after ?? [], rule: got && got.after.length ? got : null };
}

/** Of `after`'s seats ("Cat Burglar"), the open ones among `open` (seat names as Torn writes them). */
export function waitingFor(p: Pick<OcPolicy, 'order'>, crimeName: string, seatName: string, open: readonly string[]): string[] {
  const { after } = seatOrder(p, crimeName, seatName);
  if (!after.length) return [];
  return open.filter((o) => after.some((a) => (/#\s*\d+$/.test(a) ? seatKey(a) === seatKey(o) : ocKey(a) === ocKey(o))));
}

/** How much the faction wants members in this crime: 2 a named crime, 1 a named level, 0 neither. */
export function preferRank(p: Pick<OcPolicy, 'prefer'>, crimeName: string, level: number | null | undefined): number {
  if (!p.prefer) return 0;
  if (p.prefer.crimes.includes(ocKey(crimeName))) return 2;
  return typeof level === 'number' && p.prefer.levels.some(([a, b]) => level >= a && level <= b) ? 1 : 0;
}

/** The notes of every line about this seat ("CPR boost role (4m pay)"), most specific first, each once. */
export function seatNotes(p: Pick<OcPolicy, 'rules' | 'flags' | 'order'>, crimeName: string, seatName: string): string[] {
  const sc = ocKey(crimeName);
  const sk = seatKey(seatName);
  const rk = ocKey(seatName);
  const hits: Array<{ note: string; spec: number; i: number }> = [];
  let i = 0;
  for (const r of p.rules) if (r.note && !r.levels && r.role && matchesSeat(r, sc, sk, rk)) hits.push({ note: r.note, spec: specificity(r), i: i++ });
  for (const f of p.flags) if (f.note && f.reserved && matchesSeat(f, sc, sk, rk)) hits.push({ note: f.note, spec: specificity(f), i: i++ });
  for (const o of p.order) if (o.note && o.after.length && o.seats.some((s) => matchesSeat({ scenario: sc, ...s }, sc, sk, rk)) && o.scenario === sc) hits.push({ note: o.note, spec: 2, i: i++ });
  hits.sort((a, b) => b.spec - a.spec || a.i - b.i);
  return [...new Set(hits.map((h) => h.note))];
}

// ---------------------------------------------------------------- thresholds

/** Where a threshold came from, most specific first. */
export type OcLevel = 'seat' | 'role-in-crime' | 'role' | 'crime' | 'level' | 'derived' | 'general' | 'none';

export interface SeatThresholds {
  min: number | null;
  target: number | null;
  minFrom: OcLevel;
  targetFrom: OcLevel;
  /** The rule each came from (seat / role / crime / level). */
  minRule: OcRule | null;
  targetRule: OcRule | null;
  /** The target is the minimum + the policy's margin (a rule gave only a minimum). */
  targetMargin: number | null;
  /** The low/good rates the graded default used (the crime's own rule, else the general ones). */
  low: number;
  good: number;
  /** The seat's weight (% of its crime) and how it compares with the crime's average seat; null = weights unknown. */
  weight: number | null;
  share: number | null;
  /** One of the crime's leading roles (oc-roles.ts criticalKeys: "a chunk above the rest"). */
  critical: boolean;
  /** A role whose weight is under 10% of its crime ("booster"). */
  booster: boolean;
  /** The weight-graded detail behind the default minimum (null without a general/crime "low"). */
  need: SeatNeed | null;
  /** Kept for the faction's main carries (who counts: `carries`). */
  reserved: OcSeatFlag | null;
  carries: OcCarries | null;
  /** Notes from the rules about this seat ("CPR boost role (4m pay)"). */
  notes: string[];
  /** Where the minimum or target was derived from tornprobability's model: the success levels it matches, and whose. */
  derived: { minSuccess: number; targetSuccess: number; relaxed: boolean; basis: OcGoalBasis | null; source: OcRuleSource } | null;
}

const clampRate = (x: number) => Math.max(0, Math.min(100, Math.round(x)));

/**
 * What a seat needs. Each of the minimum and the target is looked up on its own, most specific first:
 *  1. a rule for this seat of this crime ("Break the Bank > Thief #2");
 *  2. a rule for this role in this crime ("Break the Bank > Thief");
 *  3. a rule for this role in any crime ("any > Thief");
 *  4. the crime's own low/good rule ("Break the Bank: 65 / 80"), graded by weight;
 *  5. a rule for the crime's level ("level 2-6: 65"), flat;
 *  5½. requirements derived from tornprobability's model to match the guidelines' chance of success (oc-requirements.ts)
 *     — not for a seat kept for the main carries (being a carry is its rule);
 *  6. the general low/good rates, graded by weight: the minimum is oc-need.ts's weight-based need, the target the same
 *     rule with half the allowed drop (low halfway to good); without the crime's weights the plain low rate / its
 *     halfway mark;
 *  7. nothing ("none") — then the seat can't be fully judged.
 * A rule that gives only a minimum sets the target too: minimum + the policy's margin (unless a more specific rule gives
 * a target). A target under the minimum is raised to it.
 */
export function seatThresholds(p: OcPolicy, crimeName: string, seatName: string, crimeWeights: Record<string, number> | null | undefined, sens?: OcSens | null, level?: number | null): SeatThresholds {
  const sc = ocKey(crimeName);
  const sk = seatKey(seatName);
  const rk = ocKey(seatName);
  const find = (f: (r: OcRule) => boolean, field: 'min' | 'target') => {
    for (let i = p.rules.length - 1; i >= 0; i--) if (f(p.rules[i]!) && p.rules[i]![field] !== null) return p.rules[i]!;
    return null;
  };
  const levels: Array<[OcLevel, (r: OcRule) => boolean]> = [
    ['seat', (r) => !r.levels && r.scenario === sc && r.seat && r.role === sk],
    ['role-in-crime', (r) => !r.levels && r.scenario === sc && !r.seat && r.role === rk],
    ['role', (r) => !r.levels && r.scenario === '' && !r.seat && r.role === rk],
  ];
  const pick = (field: 'min' | 'target') => {
    for (let i = 0; i < levels.length; i++) {
      const [lvl, f] = levels[i]!;
      const r = find(f, field);
      if (r) return { v: r[field]!, from: lvl, rule: r, at: i };
    }
    return null;
  };
  const crimeRuleMin = find((r) => !r.levels && r.scenario === sc && r.role === '', 'min');
  const crimeRuleTarget = find((r) => !r.levels && r.scenario === sc && r.role === '', 'target');
  const crimeRule = crimeRuleMin ?? crimeRuleTarget;
  const lvlOk = (r: OcRule) => !!r.levels && typeof level === 'number' && level >= r.levels[0] && level <= r.levels[1];
  const levelMin = crimeRule ? null : find(lvlOk, 'min');
  const levelTarget = crimeRule ? null : find(lvlOk, 'target');
  const low = clampRate(crimeRuleMin?.min ?? p.low);
  const good = clampRate(Math.max(low, crimeRuleTarget?.target ?? p.good));
  const need = low > 0 ? seatNeed(crimeWeights, seatName, { low, good, sens: sens ?? null }) : null;
  const needTarget = low > 0 ? seatNeed(crimeWeights, seatName, { low: (low + good) / 2, good, sens: sens ?? null }) : null;
  const gradedFrom: OcLevel = crimeRule ? 'crime' : low > 0 ? 'general' : 'none';
  const m = pick('min');
  const t = pick('target');
  const reserved = seatReserved(p, crimeName, seatName);
  const dv = !reserved && !crimeRule ? (p.derived?.crimes[tpKey(crimeName)]?.seats[tpKey(seatName)] ?? null) : null;
  const margin = p.margin;
  const plus = (v: number) => (margin === null ? null : v === 0 ? 0 : clampRate(v + margin));

  let min: number | null;
  let minFrom: OcLevel;
  let minRule: OcRule | null;
  if (m) [min, minFrom, minRule] = [m.v, m.from, m.rule];
  else if (!crimeRule && levelMin) [min, minFrom, minRule] = [levelMin.min, 'level', levelMin];
  else if (dv) [min, minFrom, minRule] = [dv.min, 'derived', null];
  else [min, minFrom, minRule] = [need?.need ?? null, need ? gradedFrom : 'none', need ? crimeRule : null];

  let target: number | null;
  let targetFrom: OcLevel;
  let targetRule: OcRule | null;
  let targetMargin: number | null = null;
  if (t && (!m || t.at <= m.at)) [target, targetFrom, targetRule] = [t.v, t.from, t.rule];
  else if (m && plus(m.v) !== null) [target, targetFrom, targetRule, targetMargin] = [plus(m.v), m.from, m.rule, m.v === 0 ? null : margin];
  else if (t) [target, targetFrom, targetRule] = [t.v, t.from, t.rule];
  else if (!crimeRule && (levelTarget || levelMin)) {
    if (levelTarget) [target, targetFrom, targetRule] = [levelTarget.target, 'level', levelTarget];
    else [target, targetFrom, targetRule, targetMargin] = [plus(levelMin!.min!), 'level', levelMin, levelMin!.min === 0 ? null : margin];
  } else if (dv) [target, targetFrom, targetRule] = [dv.target, 'derived', null];
  else [target, targetFrom, targetRule] = [needTarget?.need ?? null, needTarget ? gradedFrom : 'none', needTarget ? crimeRule : null];
  if (target !== null && min !== null && target < min) target = min;

  const w = seatWeight(crimeWeights, seatName);
  const ws = Object.values(crimeWeights ?? {}).filter((v) => typeof v === 'number' && Number.isFinite(v) && v >= 0);
  const avg = ws.length ? ws.reduce((a, b) => a + b, 0) / ws.length : 0;
  const key = crimeWeights && sk in crimeWeights ? sk : rk;
  const tag = roleTag(crimeWeights, key);
  return {
    min,
    target,
    minFrom,
    targetFrom,
    minRule,
    targetRule,
    targetMargin,
    low,
    good,
    weight: w,
    share: w !== null && avg > 0 ? w / avg : null,
    critical: tag === 'critical',
    booster: tag === 'booster',
    need,
    reserved,
    carries: p.carries,
    notes: seatNotes(p, crimeName, seatName),
    derived: p.derived && (minFrom === 'derived' || targetFrom === 'derived') ? derivedInfo(p.derived, crimeName) : null,
  };
}

function derivedInfo(d: OcDerived, crimeName: string): NonNullable<SeatThresholds['derived']> {
  const c = d.crimes[tpKey(crimeName)];
  return { minSuccess: c?.minSuccess ?? d.minSuccess, targetSuccess: c?.targetSuccess ?? d.targetSuccess, relaxed: !!c?.relaxed, basis: c?.basis ?? null, source: c?.source ?? d.source ?? 'builtin' };
}

// ---------------------------------------------------------------- the status

export type OcReason = 'below-min' | 'below-target' | 'meets-target' | 'no-rate' | 'bad-rate' | 'no-rule' | 'no-target' | 'no-min' | 'reserved' | 'carry';

export interface SeatJudgement {
  status: OcStatus;
  reason: OcReason;
  /** The pass rate judged (null when missing or invalid — never turned into 0). */
  rate: number | null;
  th: SeatThresholds;
}

/** A pass rate is a whole or decimal number from 0 to 100; anything else is missing. */
export function validRate(x: unknown): number | null {
  return typeof x === 'number' && Number.isFinite(x) && x >= 0 && x <= 100 ? x : null;
}

/**
 * The seat's status for this pass rate. `member` (when known) is who sits in it — a seat kept for the faction's main
 * carries is Risky ("reserved") for anyone who isn't one, even with a good pass rate (never better than Risky; under the
 * minimum it's Unsuitable as always).
 */
export function judgeSeat(th: SeatThresholds, rateIn: unknown, member?: number | null): SeatJudgement {
  const rate = validRate(rateIn);
  const out = (status: OcStatus, reason: OcReason): SeatJudgement => ({ status, reason, rate, th });
  if (rate === null) return out('UNVERIFIED', rateIn === null || rateIn === undefined ? 'no-rate' : 'bad-rate');
  if (th.min !== null && rate < th.min) return out('UNSUITABLE', 'below-min');
  if (th.reserved && !isCarry(th.carries, member, rate)) return out('RISKY', 'reserved');
  // A seat kept for the main carries, with no number of its own: being a carry is the rule (owner's guidelines).
  if (th.reserved && th.min === null && th.target === null) return out('SUITABLE', 'carry');
  if (th.min === null && th.target === null) return out('UNVERIFIED', 'no-rule');
  if (th.target === null) return out('UNVERIFIED', 'no-target');
  if (rate < th.target) return th.min === null ? out('UNVERIFIED', 'no-min') : out('RISKY', 'below-target');
  return out('SUITABLE', 'meets-target');
}

/** How much a seat matters next to the others (for ordering and lineup suggestions): its weight share, ×1.5 critical. */
export function seatImportance(th: SeatThresholds, p: Pick<OcPolicy, 'criticalFirst'>, level = 0): number {
  return (0.5 + Math.min(3, th.share ?? 1)) * (th.critical && p.criticalFirst ? 1.5 : 1) * (1 + Math.max(0, level) / 10);
}

/**
 * Most consequential first: unsuitable, then risky, then unverified, then suitable; within one, the critical role
 * first (when the policy says so), then heavier seats, then the bigger shortfall.
 */
export function compareJudgements(a: SeatJudgement, b: SeatJudgement, p: Pick<OcPolicy, 'criticalFirst'>): number {
  const r = STATUS_RANK[a.status] - STATUS_RANK[b.status];
  if (r) return r;
  if (p.criticalFirst && a.th.critical !== b.th.critical) return a.th.critical ? -1 : 1;
  const w = (b.th.share ?? -1) - (a.th.share ?? -1);
  if (w) return w;
  return shortfall(b) - shortfall(a);
}

function shortfall(j: SeatJudgement): number {
  if (j.rate === null) return 0;
  const bar = j.status === 'UNSUITABLE' ? j.th.min : j.th.target;
  return bar !== null ? bar - j.rate : 0;
}

// ---------------------------------------------------------------- words

const pct = (x: number) => `${Math.round(x)}%`;
const wtext = (w: number) => (w < 1 ? w.toFixed(1) : String(Math.round(w)));

/** On the seat (short, fits Torn's small tiles): "61% < 66%", "60% < 63% target", "CPR ?", "no rule", "carries only"; '' when suitable. */
export function seatTag(j: SeatJudgement): string {
  switch (j.reason) {
    case 'below-min':
      return `${j.rate}% < ${j.th.min}%`;
    case 'below-target':
      return `${j.rate}% < ${j.th.target}% target`;
    case 'reserved':
      return 'carries only';
    case 'no-rate':
    case 'bad-rate':
      return 'CPR ?';
    case 'no-rule':
    case 'no-min':
    case 'no-target':
      return 'no rule';
    default:
      return '';
  }
}

/** The seat's weight in words: "Thief #2 weighs 29% of this crime (one of its heaviest roles) · ★ a critical role". */
export function weightWords(th: SeatThresholds, seatName: string): string {
  if (th.weight === null) return `${seatName}: its weight in this crime isn’t known`;
  const kind = th.critical ? ' · ★ a critical role (well above the crime’s other roles)' : th.booster ? ' · a booster role (barely matters)' : th.share !== null && th.share >= 1.5 ? ' (one of its heaviest roles)' : th.share !== null && th.share < 0.67 ? ' (a light role)' : '';
  return `${seatName} weighs ${wtext(th.weight)}% of this crime${kind}`;
}

/** Whose rule: "your rule", "your faction’s rule", "the built-in guideline". */
const whose = (r: OcRule | null) => (r?.source === 'builtin' ? 'the built-in guideline' : r?.source === 'faction' ? 'your faction’s rule' : 'your rule');

/**
 * "derived to match the built-in guidelines: the chance of success they accept for level-8 crimes, 72% at the minimums and
 * 80% at the targets (tornprobability.com’s model)" — whose rules, which level, and the chance, in plain words.
 */
export function derivedWords(d: SeatThresholds['derived']): string {
  const pc = (x: number) => `${Math.round(x * 100)}%`;
  if (!d) return 'derived from tornprobability.com’s model';
  const at = `${pc(d.minSuccess)} at the minimums and ${pc(d.targetSuccess)} at the targets`;
  const relaxed = d.relaxed ? '; this crime can’t reach that even with 95% in every seat, so it needs the same share of its best' : '';
  if (d.basis?.kind === 'fixed') return `derived from tornprobability.com’s model for a fixed chance of success set on the Omni-Torn server: ${at}${relaxed}`;
  const who = d.source === 'faction' ? 'your faction’s rules' : d.source === 'own' ? 'your rules' : 'the built-in guidelines';
  const b = d.basis;
  const where = !b || b.kind === 'all' ? 'across their crimes (this crime’s level isn’t known)' : b.kind === 'level' ? `for level-${b.level} crimes` : b.kind === 'nearest' ? `for level-${b.level} crimes (the nearest level they cover)` : `between their level-${b.lo} and level-${b.hi} crimes`;
  return `derived to match ${who}: the chance of success they accept ${where}, ${at} (tornprobability.com’s model)${relaxed}`;
}

/** Where a threshold came from, in words. */
export function basisWords(th: SeatThresholds): string {
  const from = (lvl: OcLevel, rule: OcRule | null) =>
    lvl === 'seat' || lvl === 'role-in-crime' || lvl === 'role' || lvl === 'level'
      ? `${whose(rule)} “${rule?.text ?? ''}”`
      : lvl === 'crime'
        ? `graded by role weight from ${whose(rule)} “${rule?.text ?? ''}” (approximate)`
        : lvl === 'derived'
          ? derivedWords(th.derived)
        : lvl === 'general'
          ? th.weight === null
            ? `the general ${th.low}% rate — this crime’s role weights aren’t known (approximate)`
            : `graded by role weight from the ${th.low}% / ${th.good}% rates (approximate${th.low === OC_POLICY_DEFAULTS.low && th.good === OC_POLICY_DEFAULTS.good ? '; defaults' : ''})`
          : 'nothing set';
  const a = from(th.minFrom, th.minRule);
  const b = th.targetMargin !== null && th.targetFrom === th.minFrom && th.targetRule === th.minRule ? `the minimum + ${th.targetMargin}` : from(th.targetFrom, th.targetRule);
  return a === b ? `Basis: ${a}.` : `Basis: minimum — ${a}; target — ${b}.`;
}

/**
 * Everything leadership needs about one seat, in plain words: status, who and where, the rate, the minimum and target,
 * the role's weight and critical designation, the reason, the rules' notes and the basis (a rule — yours, your faction's
 * or the built-in guidelines — or the approximate weight-graded default; never verified crime mechanics, which
 * Omni-Torn doesn't have). A suggestion is added by the caller.
 */
export function explainSeat(j: SeatJudgement, o: { crime: string; seat: string; member?: string | null }): string {
  const who = o.member ? `${o.member} as ${o.seat}` : o.seat;
  const th = j.th;
  const bars = [th.min !== null ? `minimum ${th.min}%` : 'no minimum set', th.target !== null ? `target ${th.target}%` : 'no target set'].join(', ');
  const rate = j.rate !== null ? `${pct(j.rate)}` : j.reason === 'bad-rate' ? 'an unreadable pass rate' : 'pass rate not known';
  const carry = th.carries ? [th.carries.min !== null ? `${th.carries.min}%+ in this seat` : '', th.carries.ids.length ? `${th.carries.ids.length} listed member${th.carries.ids.length === 1 ? '' : 's'}` : ''].filter(Boolean).join(' or ') : '';
  const reason: Record<OcReason, string> = {
    'below-min': `under the minimum by ${th.min !== null && j.rate !== null ? Math.round(th.min - j.rate) : '?'} — likely to fail its checkpoints too often for this seat`,
    'below-target': 'meets the minimum but is under the preferred target',
    'meets-target': 'meets the target (no guarantee the crime succeeds)',
    'no-rate': 'can’t be fully evaluated: the pass rate isn’t known (not treated as passing)',
    'bad-rate': 'can’t be fully evaluated: the pass rate isn’t a number from 0 to 100',
    'no-rule': 'can’t be evaluated: no minimum or target applies to this seat (the “low” rate is off and no rule covers it)',
    'no-target': 'meets the minimum, but no target is set — can’t be fully evaluated',
    'no-min': 'under the target, and no minimum is set — can’t tell whether it’s unsuitable',
    reserved: `this seat is kept for the faction’s main carries${carry ? ` (${carry})` : ''}, and this member isn’t one`,
    carry: `a main carry${carry ? ` (${carry})` : ''} in a seat kept for them (no guarantee the crime succeeds)`,
  };
  const notes = th.notes.length ? ` Note: ${th.notes.join('; ')}.` : '';
  return `${OC_STATUS_LABEL[j.status]} — ${who} in ${o.crime}: ${rate} (${bars}); ${reason[j.reason]}. ${weightWords(th, o.seat)}.${notes} ${basisWords(th)}`;
}
